import { useQuery } from '@tanstack/react-query';
import { useGql } from '@ah/design';
import { Header, PillTabs, PullToRefresh, type PillTab } from '@ah/design/mobile';
import { Sparkles } from 'lucide-react';
import {
  RECOMMENDED_BOOKS_QUERY,
  type BookListItem,
  useManyBookProgress,
} from '../shared/index.ts';
import { BookRow, BookListSkeleton, ErrorRetry, olid } from './_parts.tsx';

const TABS: PillTab[] = [
  { to: '/library/books',        label: 'For You', end: true },
  { to: '/library/books/all',    label: 'All Books' },
  { to: '/library/books/recent', label: 'Read' },
  { to: '/library/books/saved',  label: 'Saved' },
];

export function BooksForYou() {
  const gql = useGql();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['books', 'for-you'],
    queryFn: async () => {
      const res = await gql.request<{ recommendedBooks: BookListItem[] }>(RECOMMENDED_BOOKS_QUERY);
      return res.recommendedBooks;
    },
  });

  const totals: Record<string, number> = {};
  for (const b of data ?? []) {
    const n = b.summary?.chapters?.length ?? 0;
    if (n > 0) totals[olid(b.olKey)] = n;
  }
  const progress = useManyBookProgress(totals);

  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header title="Books" />
      <PillTabs tabs={TABS} />
      <PullToRefresh className="flex-1 px-3 py-3" onRefresh={() => refetch()}>
        {isLoading ? (
          <BookListSkeleton />
        ) : error ? (
          <ErrorRetry label="Could not load recommendations." onRetry={() => refetch()} />
        ) : !data || data.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Sparkles size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">Personalised recommendations are building up — check back soon.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {data.map((b) => (
              <BookRow key={b.id} book={b} progress={progress[olid(b.olKey)]} />
            ))}
          </div>
        )}
      </PullToRefresh>
    </div>
  );
}
